import type { TradingHoursAdminAction } from './actions';
import type { TradingHoursAdminState } from './types';

const shallowEqual = <T extends object>(a: T, b: T): boolean =>
    Object.keys(b).every((key) => a[key as keyof T] === b[key as keyof T]);

export function tradingHoursAdminReducer(
    state: TradingHoursAdminState,
    action: TradingHoursAdminAction,
): TradingHoursAdminState {
    switch (action.type) {
        case 'SET_TAB':
            if (state.tab === action.tab) return state;
            return { ...state, tab: action.tab };
        case 'SET_VERSIONS_LIMIT':
            if (state.versionsLimit === action.limit) return state;
            return { ...state, versionsLimit: action.limit };
        case 'SELECT_VERSION':
            if (state.selectedVersionId === action.ingestionId) return state;
            return { ...state, selectedVersionId: action.ingestionId };
        case 'OPEN_CONFIRM':
            return {
                ...state,
                pendingAction: { kind: 'confirm', action: action.action, reason: action.reason, busy: false },
            };
        case 'OPEN_FREEZE':
            return {
                ...state,
                pendingAction: {
                    kind: 'freeze',
                    blacklistCurrent: action.preset.blacklistCurrent ?? true,
                    pinTo: action.preset.pinTo ?? null,
                    reason: '',
                    effectiveId: action.effectiveId,
                    busy: false,
                },
            };
        case 'OPEN_UNFREEZE':
            // Not busy while the preview loads: the operator may still cancel, and other levers stay usable.
            return { ...state, pendingAction: { kind: 'unfreeze', preview: null, busy: false } };
        case 'SET_UNFREEZE_PREVIEW':
            if (state.pendingAction?.kind !== 'unfreeze') return state;
            return { ...state, pendingAction: { kind: 'unfreeze', preview: action.preview, busy: false } };
        case 'SET_PENDING_REASON': {
            const pending = state.pendingAction;
            if (!pending || pending.kind === 'unfreeze' || pending.reason === action.reason) return state;
            return { ...state, pendingAction: { ...pending, reason: action.reason } };
        }
        case 'SET_FREEZE_OPTIONS': {
            const pending = state.pendingAction;
            if (pending?.kind !== 'freeze') return state;
            const next = { ...pending, ...action.options };
            if (shallowEqual(pending, next)) return state;
            return { ...state, pendingAction: next };
        }
        case 'SET_FREEZE_EFFECTIVE_ID': {
            const pending = state.pendingAction;
            if (pending?.kind !== 'freeze' || pending.effectiveId === action.effectiveId) return state;
            // A target equal to the new current version would be blacklisted by the same freeze.
            const pinTo = pending.blacklistCurrent && pending.pinTo === action.effectiveId ? null : pending.pinTo;
            return { ...state, pendingAction: { ...pending, effectiveId: action.effectiveId, pinTo } };
        }
        case 'SET_PENDING_BUSY': {
            const pending = state.pendingAction;
            if (!pending || pending.busy === action.busy) return state;
            return { ...state, pendingAction: { ...pending, busy: action.busy } };
        }
        case 'CLOSE_PENDING':
            if (state.pendingAction === null) return state;
            return { ...state, pendingAction: null };
        case 'SET_RECORD_FORM': {
            const next = { ...state.recordForm, ...action.form };
            if (shallowEqual(state.recordForm, next)) return state;
            return { ...state, recordForm: next };
        }
        case 'SET_RECORD_RESULT':
            if (state.recordResult === action.result) return state;
            return { ...state, recordResult: action.result };
        case 'SET_HOLIDAY_DRAFT': {
            const next = { ...state.holidayDraft, ...action.draft };
            if (shallowEqual(state.holidayDraft, next)) return state;
            return { ...state, holidayDraft: next };
        }
        case 'SUBMIT_HOLIDAY_LOOKUP': {
            const draft = { date: state.holidayDraft.date.trim(), market: state.holidayDraft.market.trim() };
            if (state.holidayLookup && shallowEqual(state.holidayLookup, draft)) return state;
            return { ...state, holidayLookup: draft };
        }
        case 'SET_HISTORY_FILTER':
            if (state.historyVersionId === action.ingestionId) return state;
            return { ...state, historyVersionId: action.ingestionId };
        case 'SET_DIFF_DRAFT': {
            const next = { ...state.diffDraft, ...action.draft };
            if (shallowEqual(state.diffDraft, next)) return state;
            return { ...state, diffDraft: next };
        }
        case 'SUBMIT_DIFF': {
            if (state.diffSelection && shallowEqual(state.diffSelection, state.diffDraft)) return state;
            return { ...state, diffSelection: { ...state.diffDraft } };
        }
        default:
            return state;
    }
}
